import {
  type CardInfo,
  CatalogChanged,
  ItemIdentityConflict,
  ItemProductTaken,
  type Scan,
  type ScanConfirmation,
  type ScanConfirmOptions,
  type SealedProduct,
} from "@hobbyshopify/backend-client";
import { BaseController, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import {
  confirmScan,
  loadScan,
  pickCatalogProduct,
  pickScanCandidate,
  refreshScanMarketPrice,
  rejectScan,
  saveScanDraft,
} from "../../core/ops/scan-ops";
import { searchLinkableSealed } from "../../core/ops/sealed-ops";
import { selectedProductId } from "../../core/primitives/candidates";
import { catalogApproval, catalogBlocker, codeKey, defaultApprovedCodes } from "../../core/primitives/catalog";
import { idleMarketRefresh } from "../../core/primitives/catalog-market";
import { errorText } from "../../core/primitives/errors";
import {
  estimateForGrade,
  type Grade,
  type GradeEstimate,
  gradeLabel,
  gradePriceCents,
} from "../../core/primitives/grades";
import { Loader } from "../../core/primitives/loader";
import { needsCandidatePick, READ_FAILED_ON_THE_WAY } from "../../core/primitives/review-row";
import {
  buildConfirmBody,
  buildCorrections,
  centsToInput,
  changedFields,
  type EditState,
  identityCorrections,
  inputToCents,
  isFloorValid,
  isMoneyValid,
  isYearValid,
  prefillPriceCents,
  rebasedEdit,
  sameIdentity,
  toEditState,
  withEdit,
} from "../../core/primitives/scan-edit";
import {
  buildSealedConfirmBody,
  buildSealedCorrections,
  draftPriceCents,
  initialLinkSearch,
  isLinkableScan,
  isSealedEditValid,
  isSealedScan,
  type SealedEditState,
  scanMarketCents,
  sealedGuides,
  suggestedSealedPriceCents,
  toSealedEditState,
} from "../../core/primitives/sealed-edit";
import { activeLocations } from "../../core/primitives/workspace";
import { ScanEditorActions, ScanEditorState } from "./scan-editor-model";

export type ScanEditorDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "locationsRepository" | "reviewQueueRepository" | "customersRepository"
>;

/** What the owning page does once the scan is answered. `price` is as confirmed, in dollars. */
export interface ScanEditorHost {
  onConfirmed(answer: { price: string }, result: ScanConfirmation): void;
  onRejected(): void;
}

/**
 * Long enough that a typed word is one save rather than six, short enough that
 * the match has moved by the time the reviewer looks up from the field.
 */
export const DRAFT_SAVE_DELAY_MS = 900;

const NO_COMPS = "No comps for this grade — check the asking price before confirming.";

type SealedPayload = { corrections?: Record<string, unknown>; priceCents?: number | null };

/**
 * Reviewing one scan: correct the reading, price it, then confirm it. A
 * section, because the review page and the scan page share it whole. A port of
 * the iOS `AdminScanEditViewModel`.
 *
 * Confirming is the only thing in the app that creates a `Card`, which is why
 * this screen is the gate rather than a formality, and why rejecting is just a
 * state change with nothing to clean up behind it.
 *
 * A grade correction saves itself a moment later, since it picks a comp of the
 * same card. Any other correction may make it a different card, so it waits
 * for "Save and reprice", and approving waits with it: confirming before would
 * confirm at the old card's price.
 *
 * Sealed product saves every correction a moment later without a lookup; the
 * reviewer asks for one with "Search again", and may confirm on the old match.
 */
export class ScanEditorController extends BaseController implements ScanEditorActions {
  readonly state = new ScanEditorState();
  #draftTimer: ReturnType<typeof setTimeout> | null = null;
  /** The corrections last sent, so an unchanged form does not save again; null after a failure, so the next edit tries again. */
  #lastSent: string | null = "{}";
  /**
   * The identity corrections the server last priced, as sent; the comps
   * describe the card until they differ. Null when nothing shows which reading
   * the price stands for, so the details wait for "Save and reprice".
   */
  #lastPriced: string | null = "{}";
  /**
   * A write threw and no read has answered since, so the screen may not hold
   * what the server does: the scan it was sent over, the fields it and any
   * like it changed, which stay the reviewer's whatever the server says, and
   * whether the price on screen stood for that scan's reading.
   */
  #unsure: { readonly was: Scan; readonly touched: ReadonlySet<string>; readonly pricedWas: boolean } | null = null;
  /** Numbers the scans shown, so an answer or a throw for one no longer on screen changes nothing. */
  #visit = 0;
  readonly #linkSearch: Loader<{ shopId: string; search: string }, { items: SealedProduct[]; matched: boolean }>;

  constructor(
    subtree: SubtreeModel,
    readonly deps: ScanEditorDeps,
    readonly host: ScanEditorHost,
    { now = () => new Date() }: { now?: () => Date } = {},
  ) {
    super();
    subtree.put(ScanEditorState, this.state);
    subtree.put(ScanEditorActions, this);
    this.#linkSearch = this.own(
      new Loader({
        status: this.state.linkStatus,
        fetch: (query: { shopId: string; search: string }) => searchLinkableSealed({ ...deps, ...query }),
        loaded: ({ items, matched }) => {
          this.state.linkCandidates.value = items;
          this.state.linkMatched.value = matched;
        },
      }),
    );

    this.sync(() => {
      this.state.quoteValidityMinutes.value = deps.storeRepository.selectedStore?.quoteValidityMinutes ?? null;
    }, [deps.storeRepository]);
    this.sync(() => {
      // The counter the app is working at first, so stock lands there unless the
      // merchant says otherwise; a booth only around its show, as the workspace picker.
      const here = deps.storeRepository.locationId;
      const all = activeLocations(deps.locationsRepository.locations, now());
      const locations = [...all.filter((l) => l.id === here), ...all.filter((l) => l.id !== here)];
      this.state.locations.value = locations;
      const chosen = this.state.stockLocationId.value;
      if (!locations.some((l) => l.id === chosen)) this.state.stockLocationId.value = locations[0]?.id ?? null;
    }, [deps.locationsRepository, deps.storeRepository]);

    this.sync(() => {
      const scan = this.state.scan.value;
      const edit = this.state.edit.value;
      const sealed = this.state.sealedEdit.value;
      const pending =
        scan !== null &&
        scan.review === "PENDING" &&
        scan.recognitionOutcome !== "no_match" &&
        (edit !== null || sealed !== null);
      const needsReprice =
        pending &&
        edit !== null &&
        (this.#lastPriced === null || JSON.stringify(identityCorrections(scan, edit)) !== this.#lastPriced);
      this.state.needsReprice.value = needsReprice;
      const blocker = !pending
        ? null
        : (catalogBlocker(scan, this.state.approvedCodes.value) ?? this.#formBlocker(scan, edit, sealed));
      this.state.confirmBlocker.value = blocker;
      // Confirming keys the card from the scan the server holds, so it waits for
      // a correction on its way there, as it waits for a pick or a reprice.
      this.state.canConfirm.value =
        pending &&
        blocker === null &&
        !needsReprice &&
        !this.state.busy.value &&
        !this.state.savingDetails.value &&
        !this.state.saving.value &&
        // A sealed confirm carries its corrections itself, so a save still waiting is dropped instead.
        !(edit !== null && this.state.draftPending.value) &&
        !this.state.searching.value &&
        this.state.picking.value === null &&
        this.state.catalogPicking.value === null;
      this.state.staleMatchWarning.value =
        pending && sealed && scan.searchStaleBecause.length > 0 ? "Product was matched before your last edits." : null;
    }, [
      this.state.scan,
      this.state.edit,
      this.state.sealedEdit,
      this.state.busy,
      this.state.savingDetails,
      this.state.saving,
      this.state.draftPending,
      this.state.searching,
      this.state.picking,
      this.state.catalogPicking,
      this.state.separateFloor,
      this.state.approvedCodes,
      this.state.quantity,
      this.state.costPerCopy,
    ]);

    this.subscribe(() => this.#formChanged(), [this.state.edit, this.state.sealedEdit]);
    this.autoDispose(() => this.#cancelDraft());
  }

  /**
   * The scan to review. Seeds the form once; later draft answers never
   * overwrite what was typed since. A new scan starts free of the last one's
   * calls, whose answers then change nothing. One form or the other, by what
   * the scan turned out to be.
   */
  seed(scan: Scan): void {
    this.#visit++;
    this.#cancelDraft();
    this.#lastSent = "{}";
    this.#lastPriced = "{}";
    this.#unsure = null;
    const unrecognised = scan.recognitionOutcome === "no_match";
    const sealed = isSealedScan(scan);
    const edit = toEditState(scan);
    const sealedEdit = sealed ? toSealedEditState(scan) : null;
    this.state.priceManual.value = scan.draftPriceCents != null;
    this.state.scan.value = scan;
    this.state.edit.value = unrecognised || sealed ? null : edit;
    this.state.sealedEdit.value = unrecognised ? null : sealedEdit;
    const floor = sealedEdit ?? edit;
    this.state.separateFloor.value = floor.floorPrice.trim() !== "" && floor.floorPrice !== floor.price;
    this.state.approvedCodes.value = defaultApprovedCodes(scan);
    this.state.quantity.value = 1;
    this.state.certNumber.value = "";
    this.state.costPerCopy.value = sealed ? centsToInput(sealedGuides(scan.scpData).dealerBuyCents) : "";
    this.state.repriceNote.value = null;
    this.state.draftError.value = null;
    this.state.picking.value = null;
    this.state.catalogPicking.value = null;
    this.state.catalogNotice.value = null;
    this.state.searching.value = false;
    this.state.marketRefresh.value = idleMarketRefresh;
    this.state.busy.value = false;
    this.state.saving.value = false;
    this.state.savingDetails.value = false;
    this.state.error.value = null;
    this.#seedLink(scan);
    this.#lastSent = sealedEdit ? JSON.stringify(this.#sealedPayload(scan)) : "{}";
  }

  change = (updates: Partial<EditState>) => {
    this.state.edit.update((prev) => (prev ? withEdit(prev, updates) : prev));
  };

  /** A cleared price goes back to following the estimate. */
  changeSealed = (updates: Partial<SealedEditState>) => {
    if (updates.price !== undefined) this.state.priceManual.value = updates.price.trim() !== "";
    this.state.sealedEdit.update((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  setPrice = (price: string) => {
    if (this.state.sealedEdit.value) {
      this.changeSealed({ price });
      return;
    }
    this.state.priceManual.value = true;
    this.state.repriceNote.value = null;
    this.change({ price });
  };

  setFloor = (floorPrice: string) => {
    if (this.state.sealedEdit.value) this.changeSealed({ floorPrice });
    else this.change({ floorPrice });
  };

  setSeparateFloor = (on: boolean) => {
    this.state.separateFloor.value = on;
    const form = this.state.sealedEdit.value ?? this.state.edit.value;
    if (on && form && !form.floorPrice.trim()) this.setFloor(form.price);
  };

  useMarketPrice = (cents: number) => {
    this.changeSealed({ price: centsToInput(cents) });
  };

  /**
   * The grade *is* which comp the card is worth, so correcting a PSA 4 reading
   * to PSA 9 and leaving the old figure is how a card sells for the price of a
   * different one. Picking overwrites the price with the comp read next to the
   * grade in the list, and says so, with the figure it replaced one tap away.
   */
  pickGrade = (grade: Grade) => {
    const raw = grade.key === "raw";
    const cents = gradePriceCents(grade.key, this.state.scan.value?.scpData);
    this.#applyGrade(raw ? "raw" : grade.label, !raw, {
      priceCents: cents ?? null,
      gradeKey: cents == null ? null : grade.key,
      approximatedFrom: null,
    });
  };

  /** A typed grade is estimated by the same rules the backend prices by. */
  commitGrade = (grade: string) => {
    const edit = this.state.edit.value;
    if (edit)
      this.#applyGrade(grade, edit.slabbed, estimateForGrade(grade, edit.slabbed, this.state.scan.value?.scpData));
  };

  /** Slabbed decides whether an unreadable grade is a raw card or an unpriceable one, so it re-prices as the grade does. */
  setSlabbed = (slabbed: boolean) => {
    const edit = this.state.edit.value;
    if (edit)
      this.#applyGrade(edit.grade, slabbed, estimateForGrade(edit.grade, slabbed, this.state.scan.value?.scpData));
  };

  undoReprice = () => {
    const note = this.state.repriceNote.value;
    if (note?.previous == null) return;
    this.change({ price: note.previous });
    this.state.repriceNote.value = null;
  };

  setQuantity = (quantity: number) => {
    this.state.quantity.value = Math.max(0, Math.floor(quantity));
  };

  setCertNumber = (cert: string) => {
    this.state.certNumber.value = cert;
  };

  setCostPerCopy = (cost: string) => {
    this.state.costPerCopy.value = cost;
  };

  setStockLocation = (locationId: string) => {
    this.state.stockLocationId.value = locationId;
  };

  toggleCode = (key: string) => {
    this.state.approvedCodes.update((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));
  };

  searchLink = (term: string) => {
    const shopId = this.deps.storeRepository.shopId;
    this.state.linkSearch.value = term;
    if (shopId !== null) this.#linkSearch.loadDebounced({ shopId, search: term });
  };

  /**
   * Never chosen for the reviewer: a similar name is not the same box. The form
   * takes the item's own price, and linking alone adds no stock: a lot is only
   * for boxes taken in now.
   */
  pickLink = (itemId: string) => {
    const item = this.state.linkCandidates.value.find((candidate) => candidate.id === itemId);
    if (!item) return;
    this.state.linkTarget.value = item;
    this.state.linkConflict.value = null;
    this.state.quantity.value = 0;
    this.changeSealed({ price: centsToInput(item.marketValueCents) });
  };

  toggleLinkName = () => {
    this.state.linkAdoptName.update((on) => !on);
  };

  /** On, the form asks the scan's market price; off, the linked item's own again. */
  toggleLinkPrice = () => {
    const scan = this.state.scan.value;
    const item = this.state.linkTarget.value;
    if (!scan || !item) return;
    const market = scanMarketCents(scan);
    if (market == null) return;
    const on = !this.state.linkAdoptPrice.value;
    this.state.linkAdoptPrice.value = on;
    this.changeSealed({ price: centsToInput(on ? market : item.marketValueCents) });
  };

  clearLink = () => {
    const scan = this.state.scan.value;
    this.state.linkTarget.value = null;
    this.state.linkConflict.value = null;
    this.state.linkAdoptName.value = false;
    this.state.linkAdoptPrice.value = false;
    if (!scan) return;
    this.state.quantity.value = 1;
    this.changeSealed({ price: centsToInput(suggestedSealedPriceCents(scan)) });
    this.state.priceManual.value = scan.draftPriceCents != null;
  };

  #seedLink(scan: Scan): void {
    this.#linkSearch.reset();
    this.state.linkTarget.value = null;
    this.state.linkConflict.value = null;
    this.state.linkAdoptName.value = false;
    this.state.linkAdoptPrice.value = false;
    this.state.linkMatched.value = true;
    this.state.linkCandidates.value = [];
    this.state.linkable.value = isLinkableScan(scan);
    this.state.linkSearch.value = this.state.linkable.value ? initialLinkSearch(scan) : "";
    const shopId = this.deps.storeRepository.shopId;
    if (this.state.linkable.value && shopId !== null) {
      void this.#linkSearch.load({ shopId, search: this.state.linkSearch.value });
    }
  }

  /**
   * The pick is one of the products the lookup already weighed, so switching
   * costs no lookup — and the estimate follows it by the same rule a re-price
   * uses, since the figure in the field was the old product's.
   */
  pick = async (productId: string) => {
    const scan = this.state.scan.value;
    const shopId = this.deps.storeRepository.shopId;
    const sent = this.state.edit.value;
    const sealed = this.state.sealedEdit.value;
    if (
      !scan ||
      (!sent && !sealed) ||
      shopId === null ||
      scan.review !== "PENDING" ||
      scan.recognitionOutcome === "no_match"
    )
      return;
    // One write to the scan at a time, so no answer lands over a newer one; and
    // never over a card correction not yet saved, which the pick would count as priced.
    if (this.#writing() || (sent && (this.state.draftPending.value || this.state.needsReprice.value))) return;
    const paused = this.#pauseDraft();
    const visit = this.#visit;
    this.state.picking.value = productId;
    this.state.draftError.value = null;
    try {
      const r = await pickScanCandidate({ ...this.deps, shopId, scanId: scan.id, productId }).catch(
        async (thrown: unknown) => {
          await this.#threw(scan, sent, visit);
          throw thrown;
        },
      );
      if (!this.#on(visit)) return;
      if (!r.ok) {
        this.state.draftError.value = errorText(r.errors) || `Could not switch the ${sealed ? "product" : "card"}.`;
        return;
      }
      if (sent) this.#took(r.value.scan, scan, sent, !this.state.priceManual.value);
      else this.#tookSealed(r.value.scan, true, paused);
    } finally {
      if (this.#on(visit)) this.state.picking.value = null;
    }
  };

  /**
   * Picking one of the products shops are split between is what settles the
   * dispute; picking a pricing candidate never does. The pick is a reading of
   * its own, so the form is seeded again from the answer.
   */
  pickCatalog = async (catalogProductId: string) => {
    const scan = this.state.scan.value;
    const shopId = this.deps.storeRepository.shopId;
    if (!scan || shopId === null || scan.review !== "PENDING" || this.#writing()) return;
    this.#cancelDraft();
    const visit = this.#visit;
    this.state.catalogPicking.value = catalogProductId;
    this.state.draftError.value = null;
    try {
      const r = await pickCatalogProduct({ ...this.deps, shopId, scanId: scan.id, catalogProductId }).catch(
        async (thrown: unknown) => {
          await this.#threw(scan, this.state.edit.value, visit);
          throw thrown;
        },
      );
      if (!this.#on(visit)) return;
      if (!r.ok) {
        this.state.draftError.value = errorText(r.errors) || "Could not pick that product.";
        return;
      }
      this.seed(r.value.scan);
      this.state.approvedCodes.value = r.value.scan.suggestedCodes
        .filter((code) => code.origin === "DEVICE")
        .map(codeKey);
    } finally {
      if (this.#on(visit)) this.state.catalogPicking.value = null;
    }
  };

  /** Searches the products again for the sealed identity as edited; a typed price stays. */
  searchAgain = async () => {
    const scan = this.state.scan.value;
    const sealed = this.state.sealedEdit.value;
    const shopId = this.deps.storeRepository.shopId;
    if (!scan || !sealed || shopId === null || scan.review !== "PENDING" || this.#writing()) return;
    this.#cancelDraft();
    const visit = this.#visit;
    this.state.searching.value = true;
    this.state.draftError.value = null;
    try {
      const r = await saveScanDraft({
        ...this.deps,
        shopId,
        scanId: scan.id,
        corrections: buildSealedCorrections(scan, sealed),
        search: true,
        priceCents: draftPriceCents(scan, sealed, this.state.priceManual.value),
      }).catch(async (thrown: unknown) => {
        await this.#threw(scan, null, visit);
        throw thrown;
      });
      if (!this.#on(visit)) return;
      if (!r.ok) {
        this.state.draftError.value = errorText(r.errors) || "Could not search again.";
        return;
      }
      this.#tookSealed(r.value.scan, true);
      if (r.value.searchFailed)
        this.state.draftError.value = "Couldn't refresh the match — the previous product is kept. Try again.";
    } finally {
      if (this.#on(visit)) this.state.searching.value = false;
    }
  };

  /** Asks the source for the shared market estimate; a price following the estimate follows the new one. */
  refreshMarket = async () => {
    const scan = this.state.scan.value;
    const shopId = this.deps.storeRepository.shopId;
    if (!scan || !this.state.sealedEdit.value || shopId === null || scan.review !== "PENDING") return;
    if (this.state.marketRefresh.value.kind === "busy" || this.#writing()) return;
    const paused = this.#pauseDraft();
    const visit = this.#visit;
    this.state.marketRefresh.value = { kind: "busy" };
    try {
      const r = await refreshScanMarketPrice({ ...this.deps, shopId, scanId: scan.id }).catch(
        async (thrown: unknown) => {
          await this.#threw(scan, null, visit);
          throw thrown;
        },
      );
      if (!this.#on(visit)) return;
      if (!r.ok) {
        this.state.marketRefresh.value = { kind: "error", message: errorText(r.errors) };
        return;
      }
      this.state.marketRefresh.value = { kind: "done", outcome: r.value.outcome };
      this.#tookSealed(r.value.scan, false, paused);
    } finally {
      if (this.#on(visit) && this.state.marketRefresh.value.kind === "busy")
        this.state.marketRefresh.value = idleMarketRefresh;
    }
  };

  /** Saves the corrected details and takes the price the server looked up for them; asked for, so it may replace a typed price, with Undo. */
  saveAndReprice = async () => {
    const scan = this.state.scan.value;
    const edit = this.state.edit.value;
    if (!scan || !edit || !this.state.needsReprice.value || !isYearValid(edit.year)) return;
    if (this.#writing()) return;
    this.#cancelDraft();
    const visit = this.#visit;
    this.state.savingDetails.value = true;
    const previous = edit.price;
    try {
      const saved = await this.#saveDraft(buildCorrections(scan, edit), true);
      const price = this.state.edit.value?.price ?? "";
      if (!this.#on(visit) || !saved || price === previous) return;
      this.state.priceManual.value = false;
      this.state.repriceNote.value = {
        previous: previous.trim() ? previous : null,
        text: price.trim()
          ? `Repriced at $${price} for the corrected card.`
          : "No comps for the corrected card. Type a price before approving.",
      };
    } finally {
      if (this.#on(visit)) this.state.savingDetails.value = false;
    }
  };

  /**
   * Confirms with the corrections, the price, and the floor (the price itself
   * unless the shop set one apart). A shop's own scan with copies stocks them
   * where the reviewer said; a customer's scan is one person asking for a price,
   * so it stocks nothing, and a card the shop already tracks keeps tracking.
   */
  confirm = async () => {
    if (!this.state.canConfirm.value) return;
    const visit = this.#visit;
    if (this.#unsure !== null) {
      // The card is keyed from the scan the server holds, so after a write
      // that threw, a read answers first, and the reviewer's own edits stand.
      this.#cancelDraft();
      this.state.busy.value = true;
      this.state.error.value = null;
      const unread = await this.#readAgain();
      if (!this.#on(visit)) return;
      this.state.busy.value = false;
      if (unread !== null) this.state.error.value = unread;
      if (unread !== null || !this.state.canConfirm.value) return;
    }
    const scan = this.state.scan.value;
    const edit = this.state.edit.value;
    const sealed = this.state.sealedEdit.value;
    const shopId = this.deps.storeRepository.shopId;
    if (!scan || (!edit && !sealed) || shopId === null) return;
    this.#cancelDraft();
    const separate = this.state.separateFloor.value;
    const form = (sealed ?? edit) as SealedEditState | EditState;
    const sent = separate ? form : { ...form, floorPrice: form.price };
    const reading = sealed
      ? buildSealedConfirmBody(scan, sent as SealedEditState)
      : buildConfirmBody(scan, sent as EditState);
    const quantity = this.state.quantity.value;
    const location = this.state.stockLocationId.value;
    const cert = sealed ? "" : this.state.certNumber.value.trim();
    const costCents = inputToCents(this.state.costPerCopy.value);
    const stock =
      scan.kind === "CUSTOMER" || quantity === 0
        ? undefined
        : {
            quantity,
            ...(location ? { locationId: location } : {}),
            ...(cert ? { certNumber: cert } : {}),
            ...(costCents != null ? { costCents } : {}),
          };
    const approval = catalogApproval(scan, this.state.approvedCodes.value);
    const linkTarget = this.state.linkTarget.value;
    const body: ScanConfirmOptions = {
      ...reading,
      ...(stock ? { stock } : {}),
      ...(approval ? { catalogApproval: approval } : {}),
      ...(linkTarget ? { linkItemId: linkTarget.id } : {}),
      ...(linkTarget && this.state.linkAdoptName.value ? { adoptScanName: true } : {}),
    };

    this.state.busy.value = true;
    this.state.error.value = null;
    try {
      const r = await confirmScan({ ...this.deps, shopId, scanId: scan.id, body }).catch((thrown: unknown) =>
        this.#thrown(
          "Confirm failed. It may have gone through: check the list before confirming again.",
          thrown,
          visit,
        ),
      );
      if (!this.#on(visit)) return;
      if (!r.ok) {
        if (r.errors.some((e) => e instanceof CatalogChanged)) {
          await this.#reloadAfterCatalogChange(shopId, scan.id, errorText(r.errors), visit);
          return;
        }
        const conflict = linkConflictMessage(r.errors);
        if (conflict) {
          this.state.linkConflict.value = conflict;
          return;
        }
        // A scan is confirmable exactly once, so a retry after a genuine failure
        // is safe but a retry after a success is refused; the message tells them apart.
        this.state.error.value = errorText(r.errors);
        return;
      }
      this.host.onConfirmed(sent, r.value);
    } finally {
      if (this.#on(visit)) this.state.busy.value = false;
    }
  };

  reject = async () => {
    const scan = this.state.scan.value;
    const shopId = this.deps.storeRepository.shopId;
    if (
      !scan ||
      shopId === null ||
      this.state.busy.value ||
      scan.review !== "PENDING" ||
      scan.recognitionOutcome === "no_match"
    )
      return;
    this.#cancelDraft();
    const visit = this.#visit;
    this.state.busy.value = true;
    this.state.error.value = null;
    try {
      const r = await rejectScan({ ...this.deps, shopId, scanId: scan.id }).catch((thrown: unknown) =>
        this.#thrown("Reject failed. It may have gone through: check the list before trying again.", thrown, visit),
      );
      if (!this.#on(visit)) return;
      if (!r.ok) {
        this.state.error.value = errorText(r.errors);
        return;
      }
      this.host.onRejected();
    } finally {
      if (this.#on(visit)) this.state.busy.value = false;
    }
  };

  #formBlocker(scan: Scan, edit: EditState | null, sealed: SealedEditState | null): string | null {
    if (sealed) {
      const asSent = this.state.separateFloor.value ? sealed : { ...sealed, floorPrice: sealed.price };
      if (!isYearValid(sealed.year)) return "Enter a valid year to confirm.";
      if (!isMoneyValid(sealed.price)) return "Enter a price, or leave it blank.";
      if (!isSealedEditValid(asSent)) return "Complete the product details to confirm.";
    } else if (edit) {
      if (!isYearValid(edit.year)) return "Enter a valid year to confirm.";
      if (!isMoneyValid(edit.price)) return "Enter a price, or leave it blank.";
      if (this.state.separateFloor.value && !isFloorValid(edit))
        return "Set the floor price at or below the asking price to confirm.";
    }
    if (needsCandidatePick(scan)) return "Pick the right match to confirm.";
    const stocking = scan.kind !== "CUSTOMER" && this.state.quantity.value > 0;
    return stocking && !isMoneyValid(this.state.costPerCopy.value) ? "Enter a cost per copy, or leave it blank." : null;
  }

  /** The backend re-read the catalogue onto the scan; the reviewer decides again from what it says now. */
  async #reloadAfterCatalogChange(shopId: string, scanId: string, notice: string, visit: number): Promise<void> {
    const fresh = await loadScan({ ...this.deps, shopId, scanId }).catch(() => null);
    if (!this.#on(visit)) return;
    if (!fresh?.ok) {
      this.state.error.value = notice;
      return;
    }
    this.state.scan.value = fresh.value;
    this.state.approvedCodes.value = defaultApprovedCodes(fresh.value);
    this.state.catalogNotice.value = notice;
  }

  /** A call that threw may still have gone through: the lists that show the scan are read again, and the throw goes on. */
  #thrown(sentence: string, thrown: unknown, visit: number): never {
    if (this.#on(visit)) this.state.error.value = sentence;
    this.deps.reviewQueueRepository.markStale();
    this.deps.customersRepository.markStale();
    throw thrown;
  }

  /** The scan a call was made for is still the one on screen. */
  #on(visit: number): boolean {
    return !this.isDisposed && this.#visit === visit;
  }

  #applyGrade(grade: string, slabbed: boolean, estimate: GradeEstimate): void {
    const edit = this.state.edit.value;
    if (!edit || this.state.scan.value?.review !== "PENDING") return;
    const price = estimate.priceCents == null ? null : (estimate.priceCents / 100).toFixed(2);
    this.change({ grade, slabbed, ...(price == null ? {} : { price }) });
    if (price === edit.price) {
      this.state.repriceNote.value = null;
    } else if (price == null) {
      // The old figure is left standing: deleting it would replace a number that
      // is merely stale with no number at all.
      this.state.repriceNote.value = { previous: null, text: NO_COMPS };
    } else {
      const from = estimate.approximatedFrom
        ? `${estimate.approximatedFrom} comps — this grade has none of its own`
        : `${gradeLabel(estimate.gradeKey ?? "")} comps`;
      this.state.repriceNote.value = {
        previous: edit.price,
        text: `Asking price re-estimated at $${price} from ${from}.`,
      };
    }
  }

  /**
   * Saves the reading while the reviewer works, so the match and the price
   * follow the correction instead of waiting for confirm. For a card only a
   * grade correction, now: any other one waits for "Save and reprice". For
   * sealed product every correction and the asking price, never a lookup.
   *
   * Debounced rather than per keystroke, and the reason is upstream: an edit to
   * a field the pricing source was queried on costs a real lookup, and that
   * queue runs at one request every two seconds shared with another app.
   *
   * A failure never blocks confirming: the draft is a convenience, and
   * `confirm` sends the corrections itself either way.
   */
  #formChanged(): void {
    const scan = this.state.scan.value;
    const edit = this.state.edit.value;
    const sealed = this.state.sealedEdit.value;
    if (!scan || (!edit && !sealed) || scan.review !== "PENDING" || scan.recognitionOutcome === "no_match") return;
    if (sealed) {
      const payload = this.#sealedPayload(scan);
      const key = JSON.stringify(payload);
      if (key === "{}" || key === this.#lastSent) {
        this.#cancelDraft();
        return;
      }
      // A half-typed year is not a correction worth a round trip, and the backend would key the item on it.
      if (!isYearValid(sealed.year)) return;
      this.#scheduleDraft(() => this.#saveSealedDraft());
      return;
    }
    if (!edit) return;
    const corrections = buildCorrections(scan, edit);
    const key = JSON.stringify(corrections);
    const unpriced = this.#lastPriced === null || JSON.stringify(identityCorrections(scan, edit)) !== this.#lastPriced;
    if (unpriced || key === "{}" || key === this.#lastSent) {
      this.#cancelDraft();
      return;
    }
    // A half-typed year is not a correction worth a round trip, and the backend would key the card on it.
    if (!isYearValid(edit.year)) return;
    this.#scheduleDraft(() => void this.#saveDraft(corrections, false));
  }

  /** Waits out the pause, and then for any other write to the scan, so no answer lands over a newer one. */
  #scheduleDraft(save: () => void): void {
    this.#cancelDraft();
    this.state.draftPending.value = true;
    this.#draftTimer = setTimeout(() => {
      this.#draftTimer = null;
      if (this.#writing()) {
        this.#scheduleDraft(save);
        return;
      }
      this.state.draftPending.value = false;
      // A read since may have shown the scan decided, and a decided scan takes no correction.
      if (this.state.scan.value?.review !== "PENDING") return;
      save();
    }, DRAFT_SAVE_DELAY_MS);
  }

  /** A request that writes the scan the screen shows is on its way, a confirm or reject among them. */
  #writing(): boolean {
    return (
      this.state.busy.value ||
      this.state.saving.value ||
      this.state.savingDetails.value ||
      this.state.searching.value ||
      this.state.picking.value !== null ||
      this.state.catalogPicking.value !== null ||
      this.state.marketRefresh.value.kind === "busy"
    );
  }

  /** True when the server took the corrections and the screen took its answer. */
  async #saveDraft(corrections: Partial<CardInfo>, reseedPrice: boolean): Promise<boolean> {
    const scan = this.state.scan.value;
    const sent = this.state.edit.value;
    const shopId = this.deps.storeRepository.shopId;
    if (!scan || !sent || shopId === null) return false;
    const visit = this.#visit;
    this.#lastSent = JSON.stringify(corrections);
    this.state.saving.value = true;
    this.state.draftError.value = null;
    try {
      const r = await saveScanDraft({ ...this.deps, shopId, scanId: scan.id, corrections }).catch(
        async (thrown: unknown) => {
          await this.#threw(scan, sent, visit);
          throw thrown;
        },
      );
      // The answer stands while a reject is on its way: if that fails, the screen still shows what the server holds.
      if (!this.#on(visit)) return false;
      if (!r.ok) {
        // Let the next edit try again rather than wedging on a stale key.
        this.#lastSent = null;
        this.state.draftError.value = errorText(r.errors) || "Could not save that change.";
        return false;
      }
      this.#took(r.value.scan, scan, sent, reseedPrice || (r.value.repriced && !this.state.priceManual.value));
      return true;
    } finally {
      if (this.#on(visit)) this.state.saving.value = false;
    }
  }

  /** Sealed identity edits never search on their own: the reviewer asks for one. */
  async #saveSealedDraft(): Promise<void> {
    const scan = this.state.scan.value;
    const shopId = this.deps.storeRepository.shopId;
    if (!scan || !this.state.sealedEdit.value || shopId === null) return;
    const visit = this.#visit;
    const payload = this.#sealedPayload(scan);
    this.#lastSent = JSON.stringify(payload);
    this.state.saving.value = true;
    this.state.draftError.value = null;
    try {
      const r = await saveScanDraft({
        ...this.deps,
        shopId,
        scanId: scan.id,
        corrections: payload.corrections ?? {},
        search: false,
        priceCents: payload.priceCents,
      }).catch(async (thrown: unknown) => {
        await this.#threw(scan, null, visit);
        throw thrown;
      });
      if (!this.#on(visit)) return;
      if (!r.ok) {
        this.#lastSent = null;
        this.state.draftError.value = errorText(r.errors) || "Could not save that change.";
        return;
      }
      this.#tookSealed(r.value.scan, r.value.repriced);
    } finally {
      if (this.#on(visit)) this.state.saving.value = false;
    }
  }

  /** What a sealed draft save sends: the corrections, and the asking price. */
  #sealedPayload(scan: Scan): SealedPayload {
    const sealed = this.state.sealedEdit.value;
    if (!sealed) return {};
    const corrections = buildSealedCorrections(scan, sealed);
    const priceCents = draftPriceCents(scan, sealed, this.state.priceManual.value);
    return {
      ...(Object.keys(corrections).length > 0 ? { corrections } : {}),
      ...(priceCents !== undefined ? { priceCents } : {}),
    };
  }

  /**
   * The scan the server answered with, the form rebased onto it. Priced and
   * sent are what the form held when the request went out, measured against the
   * answer, so an edit typed while it was on its way stays unpriced and still
   * saves.
   */
  #took(answer: Scan, was: Scan, sent: EditState, reseedPrice: boolean): void {
    const touched = this.#unsure?.was === was ? this.#unsure.touched : undefined;
    this.#unsure = null;
    const asSent = rebasedEdit(sent, sent, was, answer);
    this.#lastPriced = JSON.stringify(identityCorrections(answer, asSent));
    this.#lastSent = JSON.stringify(buildCorrections(answer, asSent));
    this.state.scan.value = answer;
    this.state.edit.update((form) => (form ? rebasedEdit(form, sent, was, answer, touched) : form));
    if (reseedPrice) {
      this.change({ price: centsToInput(prefillPriceCents(answer)) });
      this.state.repriceNote.value = null;
    }
  }

  /**
   * A sealed answer, into the screen. What the reviewer typed since stays; a
   * suggested price follows the estimate, and after a pick or a search the
   * product name follows the product.
   */
  #tookSealed(answer: Scan, productChanged: boolean, unsaved = false): void {
    this.#unsure = null;
    this.state.scan.value = answer;
    const fresh = toSealedEditState(answer);
    const price = this.state.priceManual.value ? undefined : centsToInput(suggestedSealedPriceCents(answer));
    this.state.sealedEdit.update((prev) =>
      prev
        ? {
            ...prev,
            ...(price !== undefined ? { price } : {}),
            ...(productChanged ? { productFullName: fresh.productFullName } : {}),
          }
        : prev,
    );
    this.#lastSent = unsaved ? null : JSON.stringify(this.#sealedPayload(answer));
    this.#cancelDraft();
    // A save the call put off goes now, measured against the answer.
    if (unsaved) this.#formChanged();
  }

  /** Puts off a sealed save still waiting, for a call that answers with the scan; true when there was one. */
  #pauseDraft(): boolean {
    const waiting = this.state.sealedEdit.value !== null && this.state.draftPending.value;
    if (waiting) this.#cancelDraft();
    return waiting;
  }

  /**
   * A write that threw may have gone through, so the scan is read again, and
   * the confirm and the next save measure the form against what the server
   * holds. Until a read answers, the next edit sends again whatever differs,
   * and confirming reads first.
   */
  async #threw(was: Scan, sent: EditState | null, visit: number): Promise<void> {
    if (!this.#on(visit)) return;
    this.#lastSent = null;
    this.state.draftError.value = READ_FAILED_ON_THE_WAY;
    const earlier = this.#unsure?.was === was ? this.#unsure : null;
    this.#unsure = {
      was,
      touched: new Set([...(earlier?.touched ?? []), ...(sent ? changedFields(sent, was) : [])]),
      pricedWas: earlier ? earlier.pricedWas : this.#lastPriced === "{}",
    };
    await this.#readAgain();
  }

  /** The scan read after a write that threw: null once the screen holds what the server does, else why not. */
  async #readAgain(): Promise<string | null> {
    const unsure = this.#unsure;
    const shopId = this.deps.storeRepository.shopId;
    if (unsure === null) return null;
    if (shopId === null) return READ_FAILED_ON_THE_WAY;
    const read = await loadScan({ ...this.deps, shopId, scanId: unsure.was.id }).catch(() => null);
    if (this.isDisposed || this.#unsure !== unsure) return READ_FAILED_ON_THE_WAY;
    if (!read?.ok) return (read && errorText(read.errors)) || READ_FAILED_ON_THE_WAY;
    this.#unsure = null;
    // The server writes a reading before it looks it up, so a reading the
    // write changed may not be priced: the price stands only for the reading
    // and the product the scan had, and anything else waits for "Save and reprice".
    const sameCard =
      sameIdentity(read.value, unsure.was) &&
      selectedProductId(read.value.scpData) === selectedProductId(unsure.was.scpData);
    this.#lastPriced = unsure.pricedWas && sameCard ? "{}" : null;
    const form = this.state.edit.value;
    const rebased = form ? rebasedEdit(form, form, unsure.was, read.value, unsure.touched) : null;
    // What still differs waits for the next edit, or goes with the confirm: no save is tried again by itself.
    this.#lastSent = this.state.sealedEdit.value
      ? JSON.stringify(this.#sealedPayload(read.value))
      : rebased
        ? JSON.stringify(buildCorrections(read.value, rebased))
        : "{}";
    // A decided scan takes no correction, so a write lost on the way leaves nothing to try again.
    if (read.value.review !== "PENDING") this.state.draftError.value = null;
    this.state.scan.value = read.value;
    if (rebased) this.state.edit.value = rebased;
    return null;
  }

  #cancelDraft(): void {
    if (this.#draftTimer) clearTimeout(this.#draftTimer);
    this.#draftTimer = null;
    if (!this.isDisposed) this.state.draftPending.value = false;
  }
}

const FIELD_LABELS: Record<string, string> = {
  upc: "barcode",
  catalogProductId: "catalogue product",
  productId: "product",
};

/** The refusals that belong to the picked item rather than to the scan. */
function linkConflictMessage(errors: readonly unknown[]): string | null {
  for (const e of errors) {
    if (e instanceof ItemIdentityConflict) {
      const fields = e.fields.map((field) => FIELD_LABELS[field] ?? field).join(", ");
      return `This item does not match the scan (${fields || "identity"}). Pick another, or confirm as a new item.`;
    }
    if (e instanceof ItemProductTaken) {
      return e.itemId
        ? `Another item in your inventory already holds this product (${e.itemId}). Resolve that duplicate first.`
        : "Another item in your inventory already holds this product. Resolve that duplicate first.";
    }
  }
  return null;
}
